import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CryptoCheckoutButton from "@/components/CryptoCheckoutButton";

export default function Checkout() {
    const { user } = useAuth();
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const initialStatus = params.get("status"); // "success" | "cancel" | null
    const [config, setConfig] = useState(null);
    const [paid, setPaid] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null);
    const [statusMsg, setStatusMsg] = useState(
        initialStatus === "cancel" ? "Payment cancelled. You can try again." : ""
    );
    const [method, setMethod] = useState("paypal"); // "paypal" | "crypto"

    useEffect(() => {
        api.get("/payments/config").then((r) => setConfig(r.data)).catch(() => {});
    }, []);

    if (!user || typeof user !== "object") {
        return (
            <div className="mx-auto max-w-md px-6 py-24 text-center">
                <div className="caption text-[var(--brand-accent)]">First Light</div>
                <h1 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA]">Sign in to begin boarding</h1>
                <p className="mt-3 text-[#A3A3A3]">Recruiter authentication is required before payment.</p>
            </div>
        );
    }

    if (!config) {
        return <div className="py-24 text-center text-[#A3A3A3]">Loading checkout…</div>;
    }

    const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
    const split = config.split;
    const amt = config.amount_usd.toFixed(2);
    const recruiterUSD = (config.amount_usd * split.recruiter_pct / 100).toFixed(2);
    const platformUSD = (config.amount_usd * split.platform_pct / 100).toFixed(2);
    const anchorUSD = (config.amount_usd * split.anchor_pct / 100).toFixed(2);

    return (
        <div data-testid="checkout-page" className="mx-auto max-w-3xl px-6 py-16 md:px-10">
            <div className="caption text-[var(--brand-accent)]">First Light · Boarding Fee</div>
            <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA] sm:text-6xl">
                ${amt} <span className="text-[#737373]">USD</span>
            </h1>
            <p className="mt-4 max-w-xl text-[#A3A3A3]">
                Your boarding fee unlocks the Sovereign Recruitment Hub for new recruiters.
                Payment is processed by PayPal in <span className="text-[#FAFAFA]">{config.mode}</span> mode.
            </p>

            {/* Split disclosure */}
            <div className="mt-10 rounded-md border border-white/5 bg-[#141414]/60 p-7 backdrop-blur-xl sovereign-shadow">
                <div className="caption text-[#737373]">How this is allocated</div>
                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                    <SplitTile pct={split.recruiter_pct} usd={recruiterUSD} label="Recruiter share" sub="Earned at successful placement" testId="split-recruiter" />
                    <SplitTile pct={split.platform_pct} usd={platformUSD} label="Platform" sub={split.platform_recipient} testId="split-platform" />
                    <SplitTile pct={split.anchor_pct} usd={anchorUSD} label="CyberKids anchor" sub={split.anchor_recipient || "—"} testId="split-anchor" />
                </div>
                <p className="mt-5 text-xs text-[#737373]">
                    Funds land in {config.split.platform_recipient} as a single transaction; allocations above are tracked
                    in our internal ledger and disbursed manually until automated split (PayPal Payouts API or
                    Marketplace) is enabled.
                </p>
            </div>

            {/* Payment area */}
            <div className="mt-10 rounded-md border border-white/5 bg-[#141414]/60 p-7 backdrop-blur-xl">
                {/* Method toggle */}
                {!paid && config.crypto?.enabled && (
                    <div className="mb-6 grid grid-cols-2 gap-2" data-testid="method-toggle">
                        <button
                            data-testid="method-paypal"
                            onClick={() => setMethod("paypal")}
                            className="rounded-sm border px-4 py-3 text-sm transition-all"
                            style={{
                                borderColor: method === "paypal" ? "var(--brand-accent)" : "rgba(255,255,255,0.1)",
                                background: method === "paypal" ? "rgba(201,169,110,0.10)" : "transparent",
                                color: method === "paypal" ? "var(--brand-accent)" : "#A3A3A3",
                            }}
                        >
                            <div className="font-medium">Pay with PayPal</div>
                            <div className="caption mt-1" style={{ color: "inherit" }}>Fastest Selection · $250.00</div>
                        </button>
                        <button
                            data-testid="method-crypto"
                            onClick={() => setMethod("crypto")}
                            className="rounded-sm border px-4 py-3 text-sm transition-all"
                            style={{
                                borderColor: method === "crypto" ? "var(--brand-accent)" : "rgba(255,255,255,0.1)",
                                background: method === "crypto" ? "rgba(201,169,110,0.10)" : "transparent",
                                color: method === "crypto" ? "var(--brand-accent)" : "#A3A3A3",
                            }}
                        >
                            <div className="font-medium flex items-center justify-center gap-2">
                                Pay with USDC
                                <span
                                    className="rounded-full px-2 py-0.5 text-[10px] font-semibold tracking-wider"
                                    style={{
                                        background: "var(--brand-accent)",
                                        color: "#0A0A0A",
                                    }}
                                >
                                    -{config.crypto.discount_pct}% OFF
                                </span>
                            </div>
                            <div className="caption mt-1" style={{ color: "inherit" }}>
                                Sovereign Discount · {config.crypto.amount_usdc.toFixed(2)} USDC
                            </div>
                        </button>
                    </div>
                )}

                {!paid && method === "paypal" && (
                    <div className="mb-5 text-center">
                        <div className="caption" style={{ color: "var(--brand-accent)" }}>
                            Pay with PayPal · Fastest Selection
                        </div>
                        <p className="mt-2 italic text-sm text-[#A3A3A3]">
                            "Board the Fleet instantly with PayPal. No financial blockade can stop a Sovereign signal."
                        </p>
                    </div>
                )}
                {statusMsg && (
                    <div data-testid="status-msg" className="mb-5 rounded-sm border border-white/10 bg-black/30 p-3 text-sm text-[#A3A3A3]">
                        {statusMsg}
                    </div>
                )}

                {paid && paymentResult ? (
                    <div data-testid="payment-success" className="text-center py-6">
                        <div className="caption text-[var(--brand-accent)]">Payment Confirmed</div>
                        <h2 className="mt-3 font-serif text-3xl tracking-display text-[#FAFAFA]">
                            Welcome aboard.
                        </h2>
                        <p className="mt-3 text-[#A3A3A3]">
                            Capture ID: <span className="font-mono text-xs">{paymentResult.capture_id}</span>
                        </p>
                        <p className="mt-1 text-[#A3A3A3]">
                            Amount: <span className="text-[#FAFAFA]">${paymentResult.amount_usd.toFixed(2)}</span>
                        </p>
                        {paymentResult.method === "crypto" && (
                            <p className="mt-1 caption text-[#737373]">Paid in USDC · Solana</p>
                        )}
                    </div>
                ) : !clientId ? (
                    <div className="text-center text-[#A3A3A3] py-6">
                        PayPal is not configured. Set <code>REACT_APP_PAYPAL_CLIENT_ID</code> in the frontend env.
                    </div>
                ) : method === "crypto" && config.crypto?.enabled ? (
                    <CryptoCheckoutButton
                        cryptoConfig={config.crypto}
                        onSuccess={(result) => {
                            setPaid(true);
                            setPaymentResult(result);
                        }}
                    />
                ) : (
                    <PayPalScriptProvider options={{ clientId, currency: "USD", intent: "capture", components: "buttons" }}>
                        <div data-testid="paypal-button-wrapper">
                            <PayPalButtons
                                style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                                createOrder={async () => {
                                    try {
                                        const { data } = await api.post("/payments/paypal/create-order");
                                        return data.order_id;
                                    } catch (err) {
                                        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                                        throw err;
                                    }
                                }}
                                onApprove={async (data) => {
                                    try {
                                        const { data: cap } = await api.post(`/payments/paypal/capture-order/${data.orderID}`);
                                        if (cap.status === "COMPLETED") {
                                            setPaid(true);
                                            setPaymentResult(cap);
                                            toast.success("Boarding complete.");
                                        } else {
                                            toast.error(`Payment status: ${cap.status}`);
                                        }
                                    } catch (err) {
                                        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
                                    }
                                }}
                                onCancel={() => setStatusMsg("Payment cancelled.")}
                                onError={(err) => {
                                    setStatusMsg("Payment error. Please try again.");
                                    console.error("PayPal error", err);
                                }}
                            />
                        </div>
                    </PayPalScriptProvider>
                )}
            </div>

            <p className="mt-6 caption text-[#737373]">
                Sandbox mode · use a PayPal sandbox buyer account to test
            </p>
        </div>
    );
}

function SplitTile({ pct, usd, label, sub, testId }) {
    return (
        <div data-testid={testId} className="rounded-sm border border-white/5 bg-black/20 p-4">
            <div className="flex items-baseline justify-between">
                <span className="font-serif text-2xl" style={{ color: "var(--brand-accent)" }}>{pct}%</span>
                <span className="font-mono text-sm text-[#FAFAFA]">${usd}</span>
            </div>
            <div className="mt-2 caption text-[#A3A3A3]">{label}</div>
            <div className="mt-1 text-xs text-[#737373] truncate" title={sub}>{sub}</div>
        </div>
    );
}
